import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { useSaveStripeSource } from "@/entities/stripeCheckout/hooks";
import {
  StripeSource,
  StripeSourceUpdate
} from "@/entities/stripeCheckout/types";
import { webFormErrorDetail } from "@/entities/webForm/hooks";
import {
  MAX_NAME_LENGTH,
  MAX_SOURCE_LABEL_LENGTH,
  STRIPE_CHECKOUT_PREFIX,
  sourceDraft,
  sourceErrors,
  sourceUpdate
} from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";
import Section from "@/pages/settings/components/sections/Section";

const PREFIX = `${STRIPE_CHECKOUT_PREFIX}.company`;

const FIELDS = [
  "companyNameField",
  "companyOverrideField",
  "contractOverrideField",
  "sourceLabel"
] as const;

interface CompanyIdentificationSectionProps {
  source: StripeSource | undefined;
  isLoading: boolean;
  subTitle: string;
  isBusy: boolean;
}

export const CompanyIdentificationSection: React.FC<
  CompanyIdentificationSectionProps
> = ({ source, isLoading, subTitle, isBusy }) => {
  const { t } = useTranslation();
  const saveSource = useSaveStripeSource();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [draft, setDraft] = useState(source ? sourceDraft(source) : null);
  // Show what was saved once it loads or changes, replacing any older draft.
  const saved = source ? JSON.stringify(sourceDraft(source)) : null;
  const [synced, setSynced] = useState(saved);
  if (saved !== synced) {
    setSynced(saved);
    if (source) setDraft(sourceDraft(source));
  }

  const update: StripeSourceUpdate | null =
    source && draft ? sourceUpdate(source.enabled, draft) : null;
  const errors = draft ? sourceErrors(draft) : [];
  const disabled = !source || isBusy || saveSource.isPending;
  const isDirty =
    saved !== null && draft !== null && JSON.stringify(draft) !== saved;
  const canSave = isDirty && errors.length === 0 && !disabled;

  const handleSave = () => {
    if (!update) return;
    setSaveError(null);
    saveSource.mutate(
      { data: update },
      {
        onSuccess: () => toast.success(t(`${PREFIX}.saved`)),
        onError: (error) => {
          const message = webFormErrorDetail(error) ?? t(`${PREFIX}.saveError`);
          setSaveError(message);
          toast.error(message);
        }
      }
    );
  };

  return (
    <Section
      title={t(`${PREFIX}.title`)}
      subTitle={subTitle}
      isLoading={isLoading}
      items={FIELDS.map((field) => ({
        title: t(`${PREFIX}.${field}.title`),
        subTitle: t(`${PREFIX}.${field}.subtitle`),
        customAction: (
          <Input
            data-test={`stripe-checkout-${field}`}
            aria-label={t(`${PREFIX}.${field}.title`)}
            placeholder={t(`${PREFIX}.${field}.placeholder`)}
            value={draft?.[field] ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              setDraft((current) =>
                current ? { ...current, [field]: value } : current
              );
              setSaveError(null);
            }}
            maxLength={
              field === "sourceLabel"
                ? MAX_SOURCE_LABEL_LENGTH
                : MAX_NAME_LENGTH
            }
            disabled={disabled}
            containerClassName="w-[240px]"
          />
        )
      }))}
    >
      {(errors.length > 0 || saveError) && (
        <div
          className="flex flex-col gap-1"
          role="alert"
          data-test="stripe-checkout-company-error"
        >
          {errors.map((error) => (
            <Text key={error} variant="smBodyError">
              {t(`${PREFIX}.errors.${error}`)}
            </Text>
          ))}
          {saveError && <Text variant="smBodyError">{saveError}</Text>}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!canSave}
          data-test="stripe-checkout-save-company"
        >
          {t(`${PREFIX}.save`)}
        </Button>
      </div>
    </Section>
  );
};
